import { call } from '@/lib/api';
import { groupBy } from '@/lib/helpers';
import { Document, DocumentGroups, DocumentTypeInfo } from '@/types';
import {
  ActionIcon,
  Badge,
  Box,
  Collapse,
  Divider,
  Flex,
  NavLink,
  Paper,
  PaperProps,
  Text,
  Tooltip,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { PiFolder, PiFolderOpenFill, PiQuestion } from 'react-icons/pi';
import DocumentEntry from '../DocumentsEntry/DocumentEntry';
import DocumentsUpload from '../DocumentsUpload/DocumentsUpload';
import Loader from '../Loader';

function FolderInfo({ type }: { type: DocumentTypeInfo }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ActionIcon
        variant="transparent"
        onClick={() => setOpen(!open)}
        pos="absolute"
        right={6}
        top={6}
      >
        <PiQuestion />
      </ActionIcon>
      <Collapse expanded={open}>
        <Box p="md">
          <Text size="sm" fw={500} mb="sm">
            {type.groupName}
          </Text>
          <Text size="sm" my="sm">
            {type.groupDescription}
          </Text>
          <Text size="sm" fw={500} mb="sm">
            {type.name}
          </Text>
          <Text size="sm" my="sm">
            {type.description}
          </Text>
        </Box>
      </Collapse>
    </>
  );
}

function Folders({
  documents,
  types,
  onSelect,
}: {
  documents: Document[];
  types: DocumentTypeInfo[];
  onSelect: (type: DocumentTypeInfo) => void;
}) {
  const [active, setActive] = useState(0);
  const getCount = (type: DocumentTypeInfo) =>
    documents.filter((d) => d.typeId === type.id).length;
  const entries = Object.values(groupBy(types, (t) => t.groupName)).flat();
  return entries.map((t, i) => {
    const showGroup = i === 0 || entries[i - 1].groupName !== t.groupName;
    return (
      <Fragment key={i}>
        {i != 0 && <Divider />}
        {showGroup && (
          <>
            <Text size="xs" p="5" c="dimmed">
              {t.groupName}
            </Text>

            <Divider />
          </>
        )}
        <NavLink
          label={
            <Tooltip label={t.name}>
              <Text
                size="sm"
                c={active === i ? 'blue.9' : 'gray.6'}
                truncate="end"
              >
                {t.name}
              </Text>
            </Tooltip>
          }
          leftSection={
            active === i ? (
              <PiFolderOpenFill size="1rem" />
            ) : (
              <PiFolder size="1rem" />
            )
          }
          rightSection={
            <Badge variant="light" color="gray" size="xs">
              {getCount(t)}
            </Badge>
          }
          variant="subtle"
          active={active === i}
          onClick={() => {
            onSelect(t);
            setActive(i);
          }}
        />
      </Fragment>
    );
  });
}

export default function DocumentManager({
  groups = Object.values(DocumentGroups),
  pulseId,
  ...props
}: { pulseId: string; groups?: (number | string)[] } & PaperProps) {
  const [userSelectedType, setSelectedType] = useState<DocumentTypeInfo>();

  const { isLoading: isLoadingDocuments, data: documents = [] } = useQuery<
    Document[]
  >({
    queryFn: () =>
      call<Document[]>('document/list', {
        json: { pulseId },
      }).then((res) => res.json()),
    queryKey: ['documents', 'list', pulseId],
  });

  const { isLoading: isLoadingTypes, data: types = [] } = useQuery<
    DocumentTypeInfo[]
  >({
    queryFn: () =>
      call<DocumentTypeInfo[]>('document/types', { json: { groups } }).then(
        (res) => res.json(),
      ),
    queryKey: ['documents', 'types', groups.sort().join(',')],
  });

  const selectedType = userSelectedType ?? types[0];

  if (isLoadingDocuments || isLoadingTypes || !selectedType) {
    return <Loader />;
  }

  return (
    <Paper withBorder {...props}>
      <Flex dir="row">
        <Box style={{ borderRight: '1px solid #e9ecef' }} w={250} miw={250}>
          <Folders
            documents={documents}
            types={types}
            onSelect={setSelectedType}
          />
        </Box>
        <Flex
          flex={1}
          direction="column"
          justify="space-between"
          pos="relative"
        >
          <Box bg="gray.1" mih={40}>
            <FolderInfo type={selectedType!} />
          </Box>

          <Box flex={1}>
            {documents
              .filter((d) => d.typeId === selectedType?.id)
              .map((d) => (
                <DocumentEntry key={d.id} doc={d} pulseId={pulseId} />
              ))}
          </Box>

          <Box p="md" bg="gray.1">
            {selectedType && (
              <DocumentsUpload type={selectedType.id} pulseId={pulseId} />
            )}
          </Box>
        </Flex>
      </Flex>
    </Paper>
  );
}
